import { useEffect, useRef } from "react";

import { cn } from "@/lib/utils";

/** Read --color-primary as an "r, g, b" string for canvas fills; falls back to
 * the J&J red literal if the token is missing or not a 6-digit hex. */
function readPrimaryRgb(): string {
  const fallback = "235, 23, 0"; // #eb1700
  const raw = getComputedStyle(document.documentElement).getPropertyValue("--color-primary").trim();
  const hex = /^#?([0-9a-f]{6})$/i.exec(raw)?.[1];
  if (!hex) {
    return fallback;
  }
  const int = Number.parseInt(hex, 16);
  return `${(int >> 16) & 255}, ${(int >> 8) & 255}, ${int & 255}`;
}

/**
 * A grid of soft red dots that breathes ambiently from the center and ripples
 * concentrically out from the cursor — subtle background texture for the public
 * auth pages. Pure Canvas 2D, no deps, DPR-aware.
 *
 * Respects prefers-reduced-motion: paints a single static frame instead of
 * animating, and reacts live if the OS setting is toggled (no remount needed).
 *
 * Drop in as a sibling and lift the foreground above it with `relative z-10`:
 *
 *   <div className="relative">
 *     <DotRippleBackground />
 *     <div className="relative z-10">…content…</div>
 *   </div>
 */
export function DotRippleBackground({ className }: { className?: string }) {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) {
      return;
    }
    const ctx = canvas.getContext("2d");
    if (!ctx) {
      return;
    }

    const motionQuery = window.matchMedia("(prefers-reduced-motion: reduce)");
    let reduceMotion = motionQuery.matches;

    // Tunables — kept here, not props, so the visual is consistent everywhere.
    const SPACING = 32; // px between dots (CSS pixels)
    const BASE_RADIUS = 1.1;
    const MAX_RADIUS = 4.0;
    const RIPPLE_RADIUS = 220; // cursor influence radius
    const BASE_ALPHA = 0.14;
    // Deliberately below the reference's 0.85: keeps cursor-adjacent dots as
    // texture rather than near-solid red discs, honoring our red-budget rule.
    const MAX_ALPHA = 0.55;
    const RGB = readPrimaryRgb();

    let width = 0;
    let height = 0;
    let dpr = 1;
    // Start the cursor offscreen so the first frame is calm; lerp toward the
    // real pointer for a soft "follow" feel rather than an instant snap.
    let cursorX = -10000;
    let cursorY = -10000;
    let targetX = -10000;
    let targetY = -10000;
    let raf = 0;
    const start = performance.now();

    const draw = () => {
      const t = (performance.now() - start) / 1000;

      // Ease toward the pointer so the ripple trails a hair behind the cursor —
      // organic rather than jittery.
      cursorX += (targetX - cursorX) * 0.18;
      cursorY += (targetY - cursorY) * 0.18;

      ctx.clearRect(0, 0, width, height);

      const cols = Math.ceil(width / SPACING) + 2;
      const rows = Math.ceil(height / SPACING) + 2;
      const offsetX = (width - (cols - 1) * SPACING) / 2;
      const offsetY = (height - (rows - 1) * SPACING) / 2;

      const screenCx = width / 2;
      const screenCy = height / 2;

      for (let i = 0; i < cols; i++) {
        for (let j = 0; j < rows; j++) {
          const x = offsetX + i * SPACING;
          const y = offsetY + j * SPACING;

          // Ambient: a slow concentric wave from the screen center so the grid
          // breathes even when the pointer is still.
          const dCenter = Math.hypot(x - screenCx, y - screenCy);
          const ambient = reduceMotion ? 0 : 0.5 + 0.5 * Math.cos(dCenter * 0.014 - t * 1.4);

          // Cursor ripple: concentric wave centered on the (lerped) pointer,
          // attenuated by distance — the "audio ripple" from where you hover.
          const dCursor = Math.hypot(x - cursorX, y - cursorY);
          let cursor = 0;
          if (dCursor < RIPPLE_RADIUS) {
            const falloff = 1 - dCursor / RIPPLE_RADIUS;
            const wave = 0.5 + 0.5 * Math.cos(dCursor * 0.045 - t * 5);
            // ease-out the falloff for a smoother edge; keep the wave amplitude.
            cursor = falloff * falloff * (0.55 + 0.45 * wave);
          }

          // Cursor dominates; ambient adds a soft baseline so the rest of the
          // screen stays alive too.
          const intensity = Math.min(1, cursor + ambient * 0.22);
          const radius = BASE_RADIUS + (MAX_RADIUS - BASE_RADIUS) * intensity;
          const alpha = BASE_ALPHA + (MAX_ALPHA - BASE_ALPHA) * intensity;

          ctx.fillStyle = `rgba(${RGB}, ${alpha})`;
          ctx.beginPath();
          ctx.arc(x, y, radius, 0, Math.PI * 2);
          ctx.fill();
        }
      }

      if (!reduceMotion) {
        raf = requestAnimationFrame(draw);
      }
    };

    // Reduced motion = one calm baseline frame: park the cursor offscreen first
    // so no live-hover ripple gets baked in (and repeated static repaints don't
    // creep the lerp toward the real pointer), then paint.
    const paintStaticFrame = () => {
      cursorX = -10000;
      cursorY = -10000;
      targetX = -10000;
      targetY = -10000;
      draw();
    };

    const resize = () => {
      dpr = window.devicePixelRatio || 1;
      width = window.innerWidth;
      height = window.innerHeight;
      canvas.width = Math.floor(width * dpr);
      canvas.height = Math.floor(height * dpr);
      canvas.style.width = `${width}px`;
      canvas.style.height = `${height}px`;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      // Repaint the static frame across resizes — the animation loop would
      // otherwise handle it, but it isn't running under reduced motion.
      if (reduceMotion) {
        paintStaticFrame();
      }
    };

    const onMove = (e: PointerEvent) => {
      targetX = e.clientX;
      targetY = e.clientY;
    };

    const onLeave = () => {
      targetX = -10000;
      targetY = -10000;
    };

    // React live to OS reduced-motion changes without a remount: stop the loop
    // and paint one calm static frame, or (re)start the loop.
    const onMotionChange = () => {
      reduceMotion = motionQuery.matches;
      cancelAnimationFrame(raf);
      if (reduceMotion) {
        paintStaticFrame();
      } else {
        raf = requestAnimationFrame(draw);
      }
    };

    resize();
    window.addEventListener("resize", resize);
    window.addEventListener("pointermove", onMove);
    window.addEventListener("pointerleave", onLeave);
    motionQuery.addEventListener("change", onMotionChange);
    if (reduceMotion) {
      paintStaticFrame();
    } else {
      raf = requestAnimationFrame(draw);
    }

    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener("resize", resize);
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("pointerleave", onLeave);
      motionQuery.removeEventListener("change", onMotionChange);
    };
  }, []);

  return (
    <canvas
      ref={canvasRef}
      aria-hidden
      className={cn("pointer-events-none fixed inset-0", className)}
    />
  );
}
